<script setup lang="ts">
import { BuyerReturnMutation } from '@gql/BuyerReturnMutation'
import { BuyerOrderReturnQuery } from '@gql/BuyerOrderReturnQuery'
import { BuyerOrdersList } from '@gql/BuyerOrdersList'
import type { Props as BoxProps } from '@/components/ReturnBox.vue'
import { createReturnBoxCtx } from '@/components/ReturnInfoBox.vue'
import { IconDelivery, IconFormCheck, IconBox, IconCheck } from '#components'

const { t2 } = createReturnBoxCtx()
const mobile = useMobile()
const paramsPrettyId = useParams((i) => helpers.unArray(i.id))
const { myLink } = useLinks()
const auth = useAuth()
const options = computed(() => [
  {
    title: t2('CREATE A RETURN REQUEST'),
    msg: t2('Select products from your order that you wish to return'),
    icon: IconFormCheck,
  },
  {
    title: t2('PREPARE THE PACKAGE'),
    msg: t2('Pack the items securely in a suitable box or package'),
    icon: IconBox,
  },
  {
    title: t2('SHIP THE PACKAGE'),
    msg: t2('Send the package to our return address'),
    icon: IconDelivery,
  },
  {
    title: t2('AWAIT CONFIRMATION'),
    msg: t2("Once we receive and inspect your return, we'll notify you via email and process your refund"),
    icon: IconCheck,
  },
])

const initAcountDetails = (): gen.InputSellerPayoutBank => ({
  bankAccountNumber: '',
  bankAcconutHolderName: '',
  bankSwift: '',
  bankTitle: '',
})

const data = shallowReactive({
  errors: [] as string[],
  orderSelectPageNumber: 1,
  orderSelected: undefined as undefined | gen.SellerOrderDocItemFragment,
  account: initAcountDetails(),
  products: [] as gen.InputBuyerReturnProduct[],
  state: undefined as 'product' | 'bank' | 'order' | undefined,
  inputBuyerAggree: false,
})

const { ui: orderBig } = useApolloWrap2<gen.BuyerOrderReturnQuery, gen.BuyerOrderReturnQueryVariables>(BuyerOrderReturnQuery)({
  clientId: 'post',
  vars() {
    const prettyId = paramsPrettyId.value
    return prettyId ? { input: { prettyId } } : undefined
  },
  ui: (i) => i?.buyerOrder,
})

function getOrder<T extends gen.SellerOrderDocItemFragment | gen.SellerOrderDocAndPaymentFragment>(user: typeof auth.value, id: string | None, doc: T | None): T | undefined {
  return user.isAuth && doc && doc?.prettyId === id ? doc : undefined
}
const uiOrder = computed(() => {
  const id = paramsPrettyId.value
  const user = auth.value
  const order = getOrder(user, id, orderBig.value)
  return {
    order: order || getOrder(user, id, data.orderSelected),
    products: !!order && 'returnListings' in order ? order?.returnListings : undefined,
  }
})

const [orderHolder, orderHolderSet] = useValueSynced2(() => getOrder(auth.value, paramsPrettyId.value, orderBig.value), { enabled: (n, p) => n?._id !== p?._id, deep: true })

function updateRoute(doc: None | Pick<gen.SellerOrderDocAndPaymentFragment, 'prettyId'>) {
  const orderId = doc?.prettyId
  navigateTo(orderId ? myLink('info-returns-create-id', { id: orderId }) : { name: 'info-returns-create' })
}

function saveBankDetails(values: gen.InputSellerPayoutBank) {
  data.account = {
    bankAccountNumber: helpers.required(values.bankAccountNumber),
    bankAcconutHolderName: helpers.required(values.bankAcconutHolderName),
    bankTitle: helpers.required(values.bankTitle),
    bankSwift: helpers.required(values.bankSwift),
  }
  data.state = undefined
  return null
}

const ui = computed(() => {
  const info = data.account
  const isAccountDone = !!info.bankAcconutHolderName.length && !!info.bankAccountNumber.length
  const edit = data.state
  type BoxType = BoxProps['type']
  const box1: BoxType = !auth.value.isAuth ? 'next' : orderHolder.value ? 'done' : 'active'
  const box2: BoxType = box1 !== 'done' ? 'next' : edit === 'product' ? 'active' : data.products.length ? 'done' : 'active'
  const box3: BoxType = box2 !== 'done' ? 'next' : edit === 'bank' ? 'active' : isAccountDone ? 'done' : 'active'
  const box4: BoxType = box3 === 'done' ? 'active' : 'next'
  return { box1, box2, box3, box4 }
})

const selectCont = useTemplateRef('selectCont')
const showOrders = computed((): boolean => !uiOrder.value.order?._id && !!auth.value.isAuth)

const { ui: orders } = useApolloWrap2<gen.BuyerOrdersListQuery, gen.BuyerOrdersListQueryVariables>(BuyerOrdersList)({
  clientId: 'post',
  vars() {
    if (!showOrders.value) return
    return { input: { cursor: { size: 10, number: data.orderSelectPageNumber }, get: 'Items' as const } }
  },
  ui: (i) => i?.buyerOrdersList,
})

function orderSelect(item: gen.SellerOrderDocItemFragment | undefined) {
  if (orderHolder.value?._id === item?._id) return
  data.orderSelected = item || undefined
  data.state = undefined
  data.account = initAcountDetails()
  data.products = []
  data.inputBuyerAggree = false
  orderHolderSet(undefined)
  updateRoute(item)
  nextTick(() => scrollToElem(helpers.getHTMLElement(selectCont.value), { offsetTop: 140 }))
}

const inputBoxCont = useTemplateRef('inputBoxCont')
const inputBox = useTemplateRef('inputBox')
const isView = computed(() => ui.value.box2 !== 'active')

const productInputList = computed(() => {
  const doc = orderHolder.value
  if (!doc) return []
  const oldArr = data.products
  return helpers.arr(doc?.returnListings).map((product) => {
    const productId = product.ref.product.id
    return { productId, product, input: oldArr.find((i) => i.productId === productId) }
  })
})

function saveReturnedProducts(): NotVoid {
  const arr = helpers.arr(helpers.arrify(inputBox.value), (i) => i?.out())
  const result = helpers.arr(productInputList.value, (item) => arr.find((i) => i.productId === item.productId))

  if (result.length && result.every((i) => i.reason.code)) {
    data.products = result
    data.state = undefined
    nextTick(() => scrollToElem(inputBoxCont.value, { offsetTop: 200 }))
  } else {
    const err = !result.length ? t2('Empty') : undefined
    if (err?.length) throw createError(err)
  }

  return null
}

const totalRefund = computed(() => {
  const products = orderBig.value?.returnListings
  let valid = true
  let total = 0

  for (const product of data.products) {
    const prod = helpers.arrFindId(products, product.productId, (i) => i.ref.product.id)
    const price = prod?.ref.priceUnitFinal.number
    if (!price) {
      valid = false
      total = 0
      break
    } else {
      total = helpers.priceAdd(total, helpers.priceMx(product.qty, price))
    }
  }

  if (!valid) return
  return { total: helpers.priceFormat(total) }
})

function submit(): NotVoid {
  const account = data.account
  const products2 = data.products

  if (!data.inputBuyerAggree) {
    const error = t2('Please confirm that the items are not used or damaged in any other way.')
    data.errors = [error]
    throw createError(error)
  }
  data.errors = []
  return useGqlMutate<gen.BuyerReturnMutation, gen.BuyerReturnMutationVariables>(BuyerReturnMutation, {
    clientId: 'post',
    vars: {
      input: {
        create: {
          orderId: helpers.required(orderHolder.value?._id),
          products: helpers.required(products2?.length ? products2 : null),
          paymentReturn: {
            bankAcconutHolderName: account.bankAcconutHolderName,
            bankAccountNumber: account.bankAccountNumber,
            bankSwift: account.bankSwift,
            bankTitle: account.bankTitle,
          },
        },
      },
    },
  }).then((data) => {
    const id = data?.data?.buyerReturn?.doc?.ran
    if (id) navigateTo(myLink('user-returns-id', { id }))
    return data
  })
}

const productSelectedMsg = computed(() => {
  if (!data.products.length) return t2('Select all the product from the order you want to return')
  return ts(data.products.length, data.products.length === 1 ? t2('item selected') : t2('items selected'))
})
</script>

<template>
  <AppMenu :title="t2('Create a Return')" :breadcrums="() => [{ title: t2('Returns & Refunds'), to: myLink('info-returns') }]">
    <template #content>
      <div class="_relative _br9 _c1 _glass bg">
        <div v-for="(item, index) in options" :key="item.title" :class="$style.wizz1">
          <component :is="item.icon" v-if="item.icon" :class="$style.wizzIcon" />
          <div>
            <UiUpper :title="item.title">
              <span class="_fw-xxl _fs09 m_pt02">{{ index + 1 }}.</span>
            </UiUpper>
            <div class="_ff3 _fw-xl _fs08">{{ item.msg }}</div>
          </div>
        </div>
      </div>
    </template>

    <ReturnBox class="_mt4" nr="01" :label="t2('Sign in')" :type="auth.isAuth ? 'done' : 'active'">
      <div :class="$style.pad">
        <AuthLoginUltimate2 />
      </div>
    </ReturnBox>

    <ReturnBox ref="selectCont" nr="02" :type="ui.box1" :label="t2('Retrieve your order')" :on-edit="() => updateRoute(undefined)">
      <UiEmpty :empty="showOrders && !orders?.items?.length" class="_pl2" />
      <UiGrow :open="showOrders && !!orders?.items?.length">
        <div class="_col">
          <div v-for="item in orders?.items" :key="item.prettyId" :class="[$style.box, !item.uiBox?.isAllowToReturn && 'off']">
            <OrderBox :order="item" skip-link :products="item._listingsAll" :link="false" :products-manual="undefined" />
            <div class="_pt2 _row _gap2 _ctrY _wrap" :class="$style.pad">
              <ButtonAction
                :disabled="!item.uiBox?.isAllowToReturn"
                color
                :width="mobile ? '100p' : '200'"
                :height="mobile ? '50' : '40'"
                :submit="() => unvoid(orderSelect(item))"
                :txt="t2('Return this order')"
              />
              <UiMsg v-if="!item.uiBox?.isAllowToReturn" red :txt="t2('Return not allowed for this order')" />
            </div>
          </div>
          <PaginationView class="_pt4" :change="(i) => (data.orderSelectPageNumber = i)" :input="orders?.info?.pageInfo" />
        </div>
      </UiGrow>
      <UiGrow :open="!!uiOrder.order">
        <OrderBox v-if="uiOrder.order" :order="uiOrder.order" skip-link :products="uiOrder.products" :link="false" :products-manual="undefined" />
      </UiGrow>
    </ReturnBox>

    <ReturnBox nr="03" :type="ui.box2" :label="t2('Choose items to return')" :on-edit="() => (data.state = 'product')">
      <div class="_italic _pt1 _pb1 m_pl1 d_pl2">{{ productSelectedMsg }}</div>

      <div v-if="orderHolder" ref="inputBoxCont" class="_pt2">
        <template v-for="item in productInputList" :key="item.productId">
          <ReturnProductBox v-if="!isView || item.input" ref="inputBox" :is-view="isView" :product="item.product" :order="orderHolder" :input="item.input" />
        </template>

        <div v-if="!isView" class="_pt2" :class="$style.pad">
          <ButtonAction color :width="mobile ? '100p' : '200'" :height="mobile ? '50' : '40'" :submit="() => saveReturnedProducts()" :txt="t2('Proceed with the return')" />
        </div>
      </div>
    </ReturnBox>

    <ReturnBox nr="04" :label="t2('Enter your bank account details')" :type="ui.box3" :on-edit="() => (data.state = 'bank')">
      <div :class="$style.pad">
        <ReturnBankUi v-if="ui.box3 === 'done'" :item="data.account" />
        <ReturnBankForm v-else :submit="(i) => saveBankDetails(i)" :values="data.account" />
      </div>
    </ReturnBox>

    <ReturnBox nr="05" :label="t2('Confirm your return')" :type="ui.box4">
      <div :class="$style.pad">
        <ReturnInfoBox
          :product-count="data.products.length"
          type="review"
          :order="orderBig"
          :total-refund="totalRefund?.total"
          :history="undefined"
          :order-products="orderBig?.returnListings"
          :bank="data.account"
          :products-input="data.products"
          :products="undefined"
        />
        <div class="_pt2 _pb2">
          <InputAggree v-model="data.inputBuyerAggree" type="return" :button="mobile" :right="mobile" />
        </div>

        <div v-if="data.errors" class="_pt1 _pb1">
          <UiMsg v-for="item of data.errors" :key="item" :txt="item" red />
        </div>

        <ButtonAction color :width="mobile ? '100p' : '200'" :height="mobile ? '50' : '40'" :submit="() => submit()" :txt="t2('Request refund')" />
      </div>
    </ReturnBox>
  </AppMenu>
</template>

<style module>
.pad {
  composes: global(d_pl14);
  composes: global(d_pr14);
  composes: global(m_pr08);
  composes: global(m_pl08);
}

.wizz1 {
  composes: global(_row);
  composes: global(_ctrY);
  composes: global(_pt1);
  composes: global(_pb1);
  composes: global(_pl1);
  composes: global(_pr1);
}
.wizz1:not(:last-of-type) {
  border-bottom: 2px solid var(--bg);
}
.wizzIcon {
  --svg: 28px;
  composes: global(_svg);
  composes: global(_mr2);
}
:global(.m) .wizzIcon {
  --svg: 24px;
}
.box {
  composes: global(_relative);
  composes: global(_pb14);
  composes: global(_mb-1);

  composes: global(_bw10);
  composes: global(_b5);
}
.box:global(.off) {
  opacity: 0.9;
}
</style>
